import { Button } from '@jenmusic/jen-ui-library';
import SideBarGenerateIcon from '@/assets/icons/sideBarGenerate.svg';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import SuperVoiceBf from '@/assets/images/superVoice/sv-bg.png';
import { AudioUploader } from './components/AudioUploader';
import { useForm, Controller, UseFormRegister, Control } from 'react-hook-form';
import { toast } from 'sonner';
import { useSessionStore } from '@/hooks/useSessionStore';
import { useRequest } from 'ahooks';
import { generateFromSuperVoice, SuperVoiceItemDTO } from '@/service/generatorv2';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { SuperVoiceItem } from './components/SuperVoiceList';
import { cn } from '@/utils';
import { ChevronLeft } from 'lucide-react';
import { useSuperVoiceList } from './hooks';

interface SuperVoiceFormValues {
  name: string;
  audioFiles: File[];
  superVoiceId?: number;
}

export const SuperVoiceTrackCreate = () => {
  const navigate = useNavigate();
  const { superVoiceList } = useSuperVoiceList();
  const params = useParams();
  const { id } = params;
  const initSuperVoiceId = id ? Number(id) : undefined;

  const form = useForm<SuperVoiceFormValues>({
    defaultValues: {
      name: '',
      audioFiles: [],
      superVoiceId: initSuperVoiceId,
    },
    mode: 'onChange',
  });

  const { handleSubmit, register, setValue, watch, control, formState } = form;
  const audioFiles = watch('audioFiles');
  const name = watch('name');

  const superVoiceId = watch('superVoiceId');
  console.log('superVoiceId', superVoiceId);
  // const superVoiceList: SuperVoiceItemType[] = [
  //   {
  //     id: '1',
  //     name: 'My Old SuperVoice1',
  //     description: 'SuperVoice 1 description',
  //     createdAt: new Date('2024-03-24').getTime(),
  //     isFinished: false,
  //   },
  //   {
  //     id: '2',
  //     name: 'My Old SuperVoice2',
  //     description: 'SuperVoice 2 description',
  //     createdAt: new Date('2024-03-24').getTime(),
  //     isFinished: true,
  //   },
  //   {
  //     id: '3',
  //     name: 'My Old SuperVoice3',
  //     description: 'SuperVoice 3 description',
  //     createdAt: new Date('2024-03-24').getTime(),
  //     isFinished: true,
  //   },
  //   {
  //     id: '4',
  //     name: 'My Old SuperVoice4',
  //     description: 'SuperVoice 2 description',
  //     createdAt: new Date('2024-03-24').getTime(),
  //     isFinished: true,
  //   },
  //   {
  //     id: '5',
  //     name: 'My Old SuperVoice5',
  //     description: 'SuperVoice 3 description',
  //     createdAt: new Date('2024-03-24').getTime(),
  //     isFinished: true,
  //   },
  // ];
  const superVoiceName = superVoiceList.find((item) => item.id === superVoiceId)?.name;
  console.log('superVoiceName', superVoiceName, superVoiceList, superVoiceId);

  const isFormValid = name.trim() !== '' && audioFiles.length > 0 && !!superVoiceId;

  const { run: runGenerateFromSuperVoice, loading } = useRequest(generateFromSuperVoice, {
    manual: true,
    onSuccess: (data) => {
      toast.success('SuperVoice track submitted successfully!');
      navigate('/super_voice');
    },
    onError: (error) => {
      toast.error(String(error) || 'Failed to submit SuperVoice track. Please try again.');
    },
  });

  const onSubmit = (data: SuperVoiceFormValues) => {
    const formData = new FormData();

    formData.append('name', data.name);

    data.audioFiles.forEach((file) => {
      formData.append('file', file);
    });

    runGenerateFromSuperVoice(formData, superVoiceId!);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-grow flex justify-center items-center">
        <Content
          name={superVoiceName}
          register={register}
          control={control}
          selectedSuperVoiceId={superVoiceId}
          onSelectSuperVoice={(id) => setValue('superVoiceId', id)}
          superVoiceList={superVoiceList}
        />
      </div>
      <CreateFooter isFormValid={isFormValid} onSubmit={handleSubmit(onSubmit)} isSubmitting={loading} />
    </div>
  );
};

interface ContentProps {
  register: UseFormRegister<SuperVoiceFormValues>;
  control: Control<SuperVoiceFormValues>;
  name?: string;
  selectedSuperVoiceId?: number;
  onSelectSuperVoice: (id?: number) => void;
  superVoiceList: SuperVoiceItemDTO[];
}

const Content = ({ name, register, control, selectedSuperVoiceId, onSelectSuperVoice, superVoiceList }: ContentProps) => {
  const { session } = useSessionStore();
  const { user } = session;

  const selectedSuperVoice = superVoiceList.find((item) => item.id == selectedSuperVoiceId);

  return (
    <div className="p-8 w-[796px] h-[560px] bg-jenmusic-background-tertiary rounded-2xl flex flex-col">
      <div className="typography-jenmusic-heading-large mb-6 w-[205px]">SuperVoice Track Creator️</div>

      <div className="flex gap-6">
        <div className="w-1/2">
          <Label className="font-jenmusic-GT text-jenmusic-content-tertiary text-[12px] tracking-[-0.48px] uppercase" id="name">
            NEW TRACK NAME
          </Label>
          <Input
            id="name"
            className="border border-jenmusic-border-background rounded-[8px] mt-1 h-12 mb-6"
            placeholder={'My new track'}
            {...register('name', { required: 'please input SuperVoice name' })}
          />
          <Label className="font-jenmusic-GT text-jenmusic-content-tertiary text-[12px] tracking-[-0.48px] uppercase" id="upload">
            upload a track to change
          </Label>
          <div className="mt-1">
            <Controller
              name="audioFiles"
              control={control}
              rules={{ required: true }}
              render={({ field }) => <AudioUploader maxFiles={1} onChange={(files) => field.onChange(files)} />}
            />
          </div>
        </div>

        <div className="w-1/2 mt-[-50px] px-6 flex justify-center items-center">
          {!selectedSuperVoice ? (
            <div className="w-full">
              <Label
                className=" px-6 text-jenmusic-content-primary text-[20px] leading-[28px] tracking-[-0.8px]  mb-2 block"
                id="supervoice"
              >
                Select a SuperVoice Filter
              </Label>
              <div className="mt-2 max-h-[372px] overflow-y-auto">
                {superVoiceList.map((item) => (
                  <div key={item.id} className=" mb-2 border-2 border-transparent hover:border-jenmusic-border-hover rounded-lg">
                    <SuperVoiceItem
                      item={{
                        ...item,
                        mainWord: 'USE THIS FILTER',
                        sideWord: ' ',
                      }}
                      onClickMain={() => onSelectSuperVoice(item.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="relative">
              <div
                className="w-[232px] h-[372px] rounded-xl relative"
                style={{
                  background: `url(${SuperVoiceBf})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  boxShadow:
                    '101.008px 158.919px 52.524px 0px rgba(0, 0, 0, 0.00), 64.645px 101.008px 48.484px 0px rgba(0, 0, 0, 0.03), 36.363px 56.565px 40.403px 0px rgba(0, 0, 0, 0.10), 16.161px 25.589px 29.629px 0px rgba(0, 0, 0, 0.17), 4.04px 6.734px 16.161px 0px rgba(0, 0, 0, 0.20)',
                }}
              >
                <div
                  className="absolute rounded-[5px] top-[250px] left-[20px] w-[192px] h-[72px] flex flex-col justify-center items-center"
                  style={{
                    background: 'var(--Slot-Background, linear-gradient(0deg, #191A1B 0%, #1E2021 100%))',
                  }}
                >
                  <div className="font-jenmusic-GT text-[12px] text-jenmusic-content-secondary tracking-[-0.36px] uppercase">
                    {user.name || 'ARTIST/USER NAME'}
                  </div>
                  <div className="font-jenmusic-GT text-[16px] text-[#fff] tracking-[-0.48px] uppercase">{name || 'New Track Name'}</div>
                </div>
              </div>
              <div onClick={() => onSelectSuperVoice()} className="mt-3 ml-6 flex gap-1 hover:opacity-80 cursor-pointer items-center">
                <div className=" rounded-full bg-jenmusic-content-primary w-6 h-6 flex justify-center items-center ">
                  <ChevronLeft width={16} color="#fff" />
                </div>
                <div className=" flex-grow  text-jenmusic-content-tertiary text-[16px] leading-[28px] tracking-[-0.64px]">
                  Select different filter
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface CreateFooterProps {
  isFormValid: boolean;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const CreateFooter = ({ isFormValid, onSubmit, isSubmitting }: CreateFooterProps) => {
  return (
    <div className="h-[96px] p-6 flex justify-between typography-jenmusic-small">
      <div className="flex flex-col">
        <div className="flex w-[346px] justify-between">
          <div className="w-[206px] h-4 text-jenmusic-content-tertiary">Publish Status</div>
          <div className="text-jenmusic-content-secondary w-[140px]">Private</div>
        </div>
        <div className="flex w-[346px] justify-between">
          <div className="w-[206px] h-4 text-jenmusic-content-tertiary">Estimated Jeneration Time</div>
          <div className="text-jenmusic-content-secondary w-[140px]">1mins</div>
        </div>
      </div>
      <Button
        className="px-[18px] py-[14px] h-12 text-jenmusic-interactive-contrast"
        disabled={!isFormValid || isSubmitting}
        onClick={onSubmit}
      >
        <>
          <SideBarGenerateIcon fillColor="currentColor" />
          Jenerate Track with SuperVoice<sup className="text-[8px]">TM</sup>
        </>
      </Button>
    </div>
  );
};
